/**
 * BmiCore — арифметика /bmi-calculator без DOM: взрослые пороги, центили
 * ВОЗ для детей, прибавка при беременности, единицы.
 *
 * Отдельным модулем, а не в блейде, по одной причине: каждое число здесь
 * берётся из чужого документа (ВОЗ, IOM, NHS), и сверять его надо тестом
 * с таблицей источника, а не глазом по странице (tests/js/bmi-core.test.mjs).
 */
(function (root) {
    'use strict';

    var KG_PER_LB = 0.45359237;      // точно, по определению фунта (1959)
    var CM_PER_IN = 2.54;            // точно

    /* ── Взрослые ──────────────────────────────────────────────────────
       `who` — ВОЗ, общие. `asia` — точки действия для Азиатско-Тихоокеанского
       региона (ВОЗ, 2004): 23 — повышенный риск, 27,5 — высокий. Шесть
       ступеней ожирения ВОЗ для Азии не определяла, поэтому полос четыре,
       а не шесть: «ожирение I/II/III» по 32,5 и 37,5 было бы нашей
       выдумкой, а не порогом источника. */
    var ADULT = {
        who: [
            { lo: 0, hi: 18.5, key: 'under' },
            { lo: 18.5, hi: 25, key: 'normal' },
            { lo: 25, hi: 30, key: 'over' },
            { lo: 30, hi: 35, key: 'ob1' },
            { lo: 35, hi: 40, key: 'ob2' },
            { lo: 40, hi: Infinity, key: 'ob3' },
        ],
        asia: [
            { lo: 0, hi: 18.5, key: 'under' },
            { lo: 18.5, hi: 23, key: 'normal' },
            { lo: 23, hi: 27.5, key: 'over' },
            { lo: 27.5, hi: Infinity, key: 'ob' },
        ],
    };

    function bmi(kg, cm) {
        var m = cm / 100;
        return kg / (m * m);
    }

    function adultBands(std) { return ADULT[std] || ADULT.who; }

    function bandOf(value, bands) {
        for (var i = 0; i < bands.length; i++) if (value < bands[i].hi) return bands[i];
        return bands[bands.length - 1];
    }

    /** Здоровый вес для роста: [lo, hi) в кг. hi — ИСКЛЮЧИТЕЛЬНАЯ граница:
        ИМТ 25,0 уже «избыточный», поэтому «внутри диапазона» и категория
        обязаны говорить одно и то же на самой границе. */
    function healthyKg(cm, std) {
        var n = adultBands(std)[1], m = cm / 100;
        return { lo: n.lo * m * m, hi: n.hi * m * m };
    }

    /* ── Дети: ВОЗ, LMS ────────────────────────────────────────────────
       z = ((X/M)^L − 1) / (L·S). За пределами ±3 SD ВОЗ продлевает шкалу
       ЛИНЕЙНО шагом последнего SD (таблицы: SD4 = 2·SD3 − SD2), а не
       степенной кривой: на хвосте LMS-формула сжимает z, и ребёнок с
       тяжёлым ожирением получал бы z≈3,3 вместо реальных 4+. */
    function lmsX(z, L, M, S) {
        return L === 0 ? M * Math.exp(S * z) : M * Math.pow(1 + L * S * z, 1 / L);
    }

    function zFromBmi(x, L, M, S) {
        var z = L === 0 ? Math.log(x / M) / S : (Math.pow(x / M, L) - 1) / (L * S);
        if (z > 3) {
            var sd3 = lmsX(3, L, M, S), sd23 = sd3 - lmsX(2, L, M, S);
            return 3 + (x - sd3) / sd23;
        }
        if (z < -3) {
            var sdm3 = lmsX(-3, L, M, S), sdm23 = lmsX(-2, L, M, S) - sdm3;
            return -3 + (x - sdm3) / sdm23;
        }
        return z;
    }

    function bmiAtZ(z, L, M, S) {
        if (z > 3) {
            var sd3 = lmsX(3, L, M, S);
            return sd3 + (z - 3) * (sd3 - lmsX(2, L, M, S));
        }
        if (z < -3) {
            var sdm3 = lmsX(-3, L, M, S);
            return sdm3 + (z + 3) * (lmsX(-2, L, M, S) - sdm3);
        }
        return lmsX(z, L, M, S);
    }

    /** Φ(z) — Абрамовиц–Стиган 26.2.17, ошибка < 7.5e-8. */
    function pctFromZ(z) {
        var t = 1 / (1 + 0.2316419 * Math.abs(z));
        var d = 0.3989422804014327 * Math.exp(-z * z / 2);
        var p = d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
        return z > 0 ? 1 - p : p;
    }

    /** [L, M, S] на возраст в месяцах (24…228) из таблицы who-bmi-for-age.json. */
    function lmsAt(data, sex, months) {
        if (!data || !data.lms || !data.lms[sex]) return null;
        var i = Math.round(months) - data.from_month;
        if (i < 0 || i > data.to_month - data.from_month) return null;
        return data.lms[sex][i];
    }

    /* Пороги по z. До 5 лет ВОЗ классифицирует строже к весу: «+1…+2» —
       только РИСК избыточного веса, избыточный — от +2, ожирение — от +3.
       С 5 до 19 — +1 и +2. Худоба в обоих случаях — ниже −2. */
    function childBands(months) {
        if (months < 61) {
            return [
                { lo: -Infinity, hi: -2, key: 'thin' },
                { lo: -2, hi: 1, key: 'normal' },
                { lo: 1, hi: 2, key: 'risk' },
                { lo: 2, hi: 3, key: 'over' },
                { lo: 3, hi: Infinity, key: 'obese' },
            ];
        }
        return [
            { lo: -Infinity, hi: -2, key: 'thin' },
            { lo: -2, hi: 1, key: 'normal' },
            { lo: 1, hi: 2, key: 'over' },
            { lo: 2, hi: Infinity, key: 'obese' },
        ];
    }

    /** Всё про ребёнка разом: ИМТ, z, доля сверстников ниже, полоса и
        здоровый вес [−2 SD, +1 SD) при этом росте. */
    function child(data, sex, months, kg, cm) {
        var lms = lmsAt(data, sex, months);
        if (!lms) return null;
        var L = lms[0], M = lms[1], S = lms[2], m = cm / 100, x = kg / (m * m);
        var z = zFromBmi(x, L, M, S);
        var bands = childBands(months);
        return {
            bmi: x, z: z, pct: pctFromZ(z),
            band: bandOf(z, bands), bands: bands,
            healthy: { lo: bmiAtZ(-2, L, M, S) * m * m, hi: bmiAtZ(1, L, M, S) * m * m },
            bmiAt: function (zz) { return bmiAtZ(zz, L, M, S); },
        };
    }

    /* ── Беременность: IOM 2009 ────────────────────────────────────────
       Прибавка за всю беременность по ИМТ ДО неё (пороги ВОЗ). Фунты —
       из самого документа, а не пересчётом: IOM дал их круглыми, и
       «28–40 lb» из пересчёта превратилось бы в «27,6–39,7». Для двойни
       рекомендация предварительная и для дефицита массы её нет вовсе. */
    var IOM = {
        one: {
            under: { kg: [12.5, 18], lb: [28, 40] },
            normal: { kg: [11.5, 16], lb: [25, 35] },
            over: { kg: [7, 11.5], lb: [15, 25] },
            obese: { kg: [5, 9], lb: [11, 20] },
        },
        twins: {
            under: null,
            normal: { kg: [17, 25], lb: [37, 54] },
            over: { kg: [14, 23], lb: [31, 50] },
            obese: { kg: [11, 19], lb: [25, 42] },
        },
    };

    function pregnancyClass(prePregBmi) {
        if (prePregBmi < 18.5) return 'under';
        if (prePregBmi < 25) return 'normal';
        if (prePregBmi < 30) return 'over';
        return 'obese';
    }

    function pregnancyGain(prePregBmi, twins) {
        var cls = pregnancyClass(prePregBmi);
        return { cls: cls, gain: IOM[twins ? 'twins' : 'one'][cls] };
    }

    /* ── Темп снижения ─────────────────────────────────────────────────
       NHS: безопасно 0,5–1 кг в неделю. Прежняя формула (7700 ккал на кг,
       дефицит 500 ккал) давала одно точное число, а «правило 3500 ккал»
       на длинных сроках заметно завышает потерю (Hall et al., 2011):
       диапазон честнее точки. */
    function paceWeeks(overKg) {
        return { fast: Math.max(1, Math.ceil(overKg / 1)), slow: Math.max(1, Math.ceil(overKg / 0.5)) };
    }

    /* ── Единицы ───────────────────────────────────────────────────── */
    function cmFromFtIn(ft, inch) { return ((ft || 0) * 12 + (inch || 0)) * CM_PER_IN; }
    function ftInFromCm(cm) {
        var totalIn = cm / CM_PER_IN, ft = Math.floor(totalIn / 12), inch = totalIn - ft * 12;
        // 5 ft 11.96 in округляется в 5 ft 12 in — переносим в следующий фут.
        if (Math.round(inch * 10) / 10 >= 12) { ft += 1; inch = 0; }
        return { ft: ft, inch: inch };
    }
    function kgFromLb(lb) { return lb * KG_PER_LB; }
    function lbFromKg(kg) { return kg / KG_PER_LB; }
    function kgFromStLb(st, lb) { return ((st || 0) * 14 + (lb || 0)) * KG_PER_LB; }
    function stLbFromKg(kg) {
        var totalLb = kg / KG_PER_LB, st = Math.floor(totalLb / 14), lb = totalLb - st * 14;
        if (Math.round(lb) >= 14) { st += 1; lb = 0; }
        return { st: st, lb: lb };
    }

    /** Рост «1.7» в поле сантиметров — это метры, а не карлик ростом 1,7 см.
        Ниже 3 считаем метрами; от 3 до 50 — не рост вовсе. */
    function heightCm(raw) {
        if (!(raw > 0)) return { cm: NaN, meters: false };
        if (raw < 3) return { cm: raw * 100, meters: true };
        return { cm: raw, meters: false };
    }

    var api = {
        KG_PER_LB: KG_PER_LB, CM_PER_IN: CM_PER_IN,
        bmi: bmi, adultBands: adultBands, bandOf: bandOf, healthyKg: healthyKg,
        lmsAt: lmsAt, zFromBmi: zFromBmi, bmiAtZ: bmiAtZ, pctFromZ: pctFromZ,
        childBands: childBands, child: child,
        pregnancyClass: pregnancyClass, pregnancyGain: pregnancyGain, paceWeeks: paceWeeks,
        cmFromFtIn: cmFromFtIn, ftInFromCm: ftInFromCm, kgFromLb: kgFromLb, lbFromKg: lbFromKg,
        kgFromStLb: kgFromStLb, stLbFromKg: stLbFromKg, heightCm: heightCm,
    };
    root.BmiCore = api;
    if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
